/**
 * BAT 配置一键生成抽屉（Phase 2：规则推导 + LLM 增强，可编辑预览 + 确认保存）
 *
 * 挂载于 BCR 测试页：用户点 [⚙ 生成/更新运行配置] → 本抽屉
 *  1. 先查项目是否被锁定（bvp_bat_config_locks），锁定则直接提示并终止
 *  2. 调 bsp_back generate-from-bcr → bvp_back 规则推导 + LLM 增强
 *  3. 分块展示：基础信息（可编辑，带来源徽标）/ 规则确认（逐产物，可切换 RMP 部署）
 *     / 台架检查配置（bench check）/ 说明
 *  4. 用户按需补充/修改后 [保存]（二次确认弹窗）→ bsp_back save → bvp_back 写入
 *
 * 注：推导结果作为「默认值」预填，用户只需在页面上确认；规则部分可逐产物切换
 *     「是否默认 RMP 部署」（关 = kind 置 SKIP），无需跳转到 BVP 配置中心。
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Col,
  Collapse,
  Divider,
  Drawer,
  Empty,
  Input,
  Modal,
  Row,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  CopyOutlined,
  ReloadOutlined,
  RobotOutlined,
  SaveOutlined,
} from '@ant-design/icons';

import {
  discoverOtaEnv,
  generateBatConfigFromBcr,
  getBatConfigLock,
  saveBatConfig,
} from '@/apis/batConfig';
import type {
  BatConfig,
  BatConfigFieldSource,
  BatConfigGenerateResult,
  OtaEnvCandidate,
} from '@/apis/batConfig';

const { Text, Paragraph } = Typography;

interface BatConfigGenerateDrawerProps {
  open: boolean;
  onClose: () => void;
  /** BSP 项目名（bvp_bat_config.project） */
  project: string;
  /** BSP 包 id，用于取包内文件结构 */
  packageId?: number;
  /** 多车型项目必填 */
  vehicleModel?: string;
  /** 保存成功后回调（父页面可刷新状态） */
  onSaved?: () => void;
}

/** 字段来源 → 徽标文案 + 颜色 */
const SOURCE_META: Record<BatConfigFieldSource, { text: string; color: string }> = {
  bmp: { text: 'BMP台架', color: 'blue' },
  itt_config: { text: 'ITT配置', color: 'green' },
  existing: { text: '历史配置', color: 'purple' },
  default: { text: '默认值', color: 'default' },
  rcc_derived: { text: 'RMP推导', color: 'cyan' },
  bsp_package: { text: 'BSP包', color: 'geekblue' },
  bcr_context: { text: 'BCR上下文', color: 'orange' },
  llm_suggested: { text: 'LLM建议', color: 'magenta' },
};

const MODE_META: Record<string, { text: string; type: 'info' | 'success' | 'warning' }> = {
  create: { text: '新建模式：该项目尚无 BAT 配置，将新建', type: 'success' },
  update: { text: '更新模式：该项目已有 BAT 配置，保存将覆盖', type: 'info' },
  locked: { text: '该项目在 BVP 配置中心已锁定', type: 'warning' },
};

/**
 * 基础信息中可编辑的字段（推导结果作为默认值预填）。
 * required=true 表示准入必需（缺失时对应 stage 会直接报错），标签后追加红色 *。
 */
const EDITABLE_BASIC_FIELDS: Array<{
  path: string;
  label: string;
  placeholder?: string;
  required?: boolean;
}> = [
  { path: 'bench_name', label: '台架', placeholder: '如 26E-WYUES19', required: true },
  { path: 'vehicle_model', label: '车型', placeholder: '多车型项目必填', required: true },
  { path: 'mountain', label: '山头' },
  { path: 'ota_env', label: 'RMP OTA Environment', placeholder: '如 hnas_ct' },
  { path: 'project_alias.soc', label: 'BVP准入工程名(SOC)', required: true },
  { path: 'project_alias.mcu', label: 'BVP准入工程名(MCU)', required: true },
];

/** 取字段来源徽标；支持 'project_alias.mcu' 这类嵌套 key（逐级前缀回退） */
const resolveSource = (
  fieldSources: Record<string, BatConfigFieldSource> | undefined,
  path: string
): BatConfigFieldSource | undefined => {
  if (!fieldSources) return undefined;
  if (fieldSources[path]) return fieldSources[path];
  const parts = path.split('.');
  while (parts.length > 1) {
    parts.pop();
    const prefix = parts.join('.');
    if (fieldSources[prefix]) return fieldSources[prefix];
  }
  return undefined;
};

/** 按 'a.b.c' 路径读取嵌套值 */
const getByPath = (obj: any, path: string): any =>
  path.split('.').reduce((acc, key) => (acc === undefined || acc === null ? undefined : acc[key]), obj);

/** 按 'a.b.c' 路径写入嵌套值（不可变，返回新对象） */
const setByPath = (obj: any, path: string, value: any): any => {
  const parts = path.split('.');
  const clone: any = Array.isArray(obj) ? [...obj] : { ...(obj || {}) };
  let cursor = clone;
  for (let i = 0; i < parts.length - 1; i += 1) {
    const key = parts[i];
    const next = cursor[key];
    cursor[key] = Array.isArray(next) ? [...next] : { ...(next || {}) };
    cursor = cursor[key];
  }
  cursor[parts[parts.length - 1]] = value;
  return clone;
};

/**
 * 该产物生效的 RMP 类型：rmp_rules[key].kind 优先（SKIP 视为未设置，回退 kind_map）
 * 用于勾选「默认 RMP 部署」时回填真实 kind（而非 SKIP）
 */
const resolveDeployKind = (
  key: string,
  kindMap?: Record<string, string>,
  rmpRules?: Record<string, { kind?: string }>
): string => {
  const fromRule = String(rmpRules?.[key]?.kind || '').toUpperCase();
  if (fromRule && fromRule !== 'SKIP') return fromRule;
  return String(kindMap?.[key] || '').toUpperCase();
};

/** 该产物当前是否勾选「默认 RMP 部署」（rmp_rules[key].kind === 'SKIP' 视为未勾选） */
const isDeployEnabled = (
  key: string,
  kindMap?: Record<string, string>,
  rmpRules?: Record<string, { kind?: string }>
): boolean => {
  const fromRule = String(rmpRules?.[key]?.kind || '').toUpperCase();
  if (fromRule) return fromRule !== 'SKIP';
  return Boolean(kindMap?.[key]);
};

const SourceTag = ({ source }: { source?: BatConfigFieldSource }) => {
  if (!source) return null;
  const meta = SOURCE_META[source] || { text: source, color: 'default' };
  return (
    <Tag color={meta.color} style={{ marginInlineStart: 6 }}>
      {meta.text}
    </Tag>
  );
};

/** 只读展示单个值（用于项目名等不可改字段） */
const FieldValue = ({ value, source }: { value: any; source?: BatConfigFieldSource }) => (
  <Space size={4} wrap>
    <Text>{value === undefined || value === null || value === '' ? '—' : String(value)}</Text>
    <SourceTag source={source} />
  </Space>
);

/** 可编辑字段：标签 + 来源徽标 + 输入框；extra 为标签行右侧的附加操作（如「获取」按钮） */
const EditableField = ({
  label,
  value,
  source,
  placeholder,
  extra,
  required,
  onChange,
}: {
  label: ReactNode;
  value: any;
  source?: BatConfigFieldSource;
  placeholder?: string;
  extra?: ReactNode;
  required?: boolean;
  onChange: (value: string) => void;
}) => (
  <div>
    <div
      style={{
        marginBottom: 4,
        fontSize: 12,
        color: '#595959',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Space size={0}>
        <span>{label}</span>
        {required ? <span style={{ color: '#ff4d4f', marginInlineStart: 2 }}>*</span> : null}
        <SourceTag source={source} />
      </Space>
      {extra}
    </div>
    <Input
      value={value === undefined || value === null ? '' : String(value)}
      placeholder={placeholder}
      allowClear
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

/**
 * 复杂值（对象/数组）编辑：JSON 文本域，实时解析，格式错不落值；
 * 打字过程中不因父级回写而重置文本，避免光标跳动。
 */
const JsonValueField = ({
  value,
  onChange,
  placeholder,
}: {
  value: any;
  onChange: (value: any) => void;
  placeholder?: string;
}) => {
  const [text, setText] = useState<string>(() => JSON.stringify(value ?? {}, null, 2));
  const [parseError, setParseError] = useState<string | null>(null);
  const emittedRef = useRef<string>('');

  useEffect(() => {
    const incoming = JSON.stringify(value ?? {});
    // 若本次变化由本输入框自身触发，保留用户原文，避免光标跳动
    if (emittedRef.current && emittedRef.current === incoming) return;
    setText(JSON.stringify(value ?? {}, null, 2));
    setParseError(null);
  }, [value]);

  return (
    <Space orientation="vertical" size={4} style={{ display: 'flex' }}>
      <Input.TextArea
        rows={8}
        value={text}
        placeholder={placeholder}
        onChange={(e) => {
          const raw = e.target.value;
          setText(raw);
          try {
            const parsed = JSON.parse(raw);
            emittedRef.current = JSON.stringify(parsed ?? {});
            onChange(parsed);
            setParseError(null);
          } catch {
            setParseError('JSON 格式错误，暂未应用该值');
          }
        }}
      />
      {parseError ? (
        <Text type="danger" style={{ fontSize: 12 }}>
          {parseError}
        </Text>
      ) : null}
    </Space>
  );
};

const JsonBlock = ({ data }: { data: any }) => (
  <pre
    style={{
      background: '#f5f5f5',
      padding: 12,
      borderRadius: 4,
      overflow: 'auto',
      maxHeight: 320,
      margin: 0,
      fontSize: 12,
    }}
  >
    {JSON.stringify(data ?? {}, null, 2)}
  </pre>
);

const BatConfigGenerateDrawer = ({
  open,
  onClose,
  project,
  packageId,
  vehicleModel,
  onSaved,
}: BatConfigGenerateDrawerProps) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [locked, setLocked] = useState(false);
  const [result, setResult] = useState<BatConfigGenerateResult | null>(null);
  const [formValues, setFormValues] = useState<BatConfig>({} as BatConfig);
  const [error, setError] = useState<string | null>(null);
  const [otaEnvLoading, setOtaEnvLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [otaEnvCandidates, setOtaEnvCandidates] = useState<OtaEnvCandidate[]>([]);
  const [otaEnvModalOpen, setOtaEnvModalOpen] = useState(false);

  const load = useCallback(async () => {
    if (!project) return;
    setLoading(true);
    setError(null);
    setResult(null);
    setLocked(false);
    try {
      // 前置检查：项目是否被锁定
      const isLocked = await getBatConfigLock(project);
      if (isLocked) {
        setLocked(true);
        return;
      }
      const data = await generateBatConfigFromBcr({
        project,
        package_id: packageId,
        vehicle_model: vehicleModel,
        use_llm: true, // Phase 2：规则推导 + LLM 增强（失败自动降级，见 llm_degraded_reason）
      });
      if (!data) {
        setError('后端未返回生成结果');
        return;
      }
      if (data.locked) {
        setLocked(true);
        return;
      }
      setResult(data);
      setFormValues({ ...(data.config || {}) });
    } catch (e: any) {
      const detail =
        e?.response?.data?.error ||
        e?.response?.data?.detail ||
        e?.message ||
        '生成失败';
      setError(String(detail));
    } finally {
      setLoading(false);
    }
  }, [project, packageId, vehicleModel]);

  useEffect(() => {
    if (open) {
      load();
    }
  }, [open, load]);

  const handleFieldChange = useCallback((path: string, value: any) => {
    setFormValues((prev) => setByPath(prev, path, value));
  }, []);

  /** 逐产物确认：合并 kind_map / rmp_rules / pkg_rules 的产物 key */
  const artifacts = useMemo(() => {
    const keys = new Set<string>([
      ...Object.keys(formValues?.kind_map || {}),
      ...Object.keys(formValues?.rmp_rules || {}),
      ...Object.keys(formValues?.pkg_rules || {}),
    ]);
    return Array.from(keys);
  }, [formValues?.kind_map, formValues?.rmp_rules, formValues?.pkg_rules]);

  /** 切换某产物是否默认 RMP 部署：关 → kind 置 SKIP（仍注册 RMP，仅跳过独立部署） */
  const handleToggleDeploy = useCallback(
    (key: string, deploy: boolean) => {
      const baseKind =
        resolveDeployKind(key, formValues?.kind_map, formValues?.rmp_rules) || 'MCU';
      handleFieldChange(`rmp_rules.${key}.kind`, deploy ? baseKind : 'SKIP');
    },
    [formValues?.kind_map, formValues?.rmp_rules, handleFieldChange]
  );

  /**
   * BCR运行配置AI自动生成：带上用户当前填写的基础信息（含刚补的 ota_env / 台架 / 车型）
   * 重新调一次 LLM 规则推理，只替换 kind_map / rmp_rules / pkg_rules，
   * 基础信息与 bench check 配置保持用户已改的值。
   */
  const handleAiGenerate = useCallback(async () => {
    if (!project) return;
    setAiLoading(true);
    try {
      const benchName = String(getByPath(formValues, 'bench_name') ?? '').trim();
      const otaEnv = String(getByPath(formValues, 'ota_env') ?? '').trim();
      const model = String(getByPath(formValues, 'vehicle_model') ?? '').trim();
      const data = await generateBatConfigFromBcr({
        project,
        package_id: packageId,
        vehicle_model: model || vehicleModel,
        bench_name: benchName || undefined,
        ota_env: otaEnv || undefined,
        use_llm: true,
      });
      if (!data) {
        message.error('后端未返回生成结果');
        return;
      }
      if (data.locked) {
        setLocked(true);
        return;
      }
      setResult(data);
      setFormValues((prev) => ({
        ...prev,
        kind_map: data.config?.kind_map ?? {},
        rmp_rules: data.config?.rmp_rules ?? {},
        pkg_rules: data.config?.pkg_rules ?? {},
      }));
      const artifactCount = new Set([
        ...Object.keys(data.config?.kind_map || {}),
        ...Object.keys(data.config?.rmp_rules || {}),
        ...Object.keys(data.config?.pkg_rules || {}),
      ]).size;
      if (data.llm_used) {
        message.success(`AI 规则生成完成（${artifactCount} 个产物），请在下方确认完整 JSON`);
      } else {
        message.warning(
          `AI 未生效，已使用纯规则推导（${artifactCount} 个产物）：${
            data.llm_degraded_reason || 'LLM 未启用'
          }`
        );
      }
    } catch (e: any) {
      const detail =
        e?.response?.data?.error || e?.response?.data?.detail || e?.message || '生成失败';
      message.error(`AI 规则生成失败：${detail}`);
    } finally {
      setAiLoading(false);
    }
  }, [project, packageId, vehicleModel, formValues]);

  const applyOtaEnv = useCallback(
    (otaEnv: string) => {
      handleFieldChange('ota_env', otaEnv);
      setOtaEnvModalOpen(false);
      message.success(`已填入 RMP OTA Environment：${otaEnv}`);
    },
    [handleFieldChange]
  );

  /**
   * 点击「获取」：用项目名 / 台架名从 RMP 反查候选 ota_env（RMP OTA Environment）
   * 命中 1 个 → 直接填入；多个 → 弹窗选择；0 个 → 提示手动填写。
   */
  const handleDiscoverOtaEnv = useCallback(async () => {
    const proj = String(getByPath(formValues, 'project') ?? project ?? '').trim();
    const bench = String(getByPath(formValues, 'bench_name') ?? '').trim();
    if (!proj && !bench) {
      message.warning('请先填写项目或台架，再获取 RMP OTA Environment');
      return;
    }
    setOtaEnvLoading(true);
    try {
      const candidates = await discoverOtaEnv(proj, bench);
      if (!candidates || candidates.length === 0) {
        message.warning('未匹配到 RMP OTA Environment，请手动填写');
        return;
      }
      if (candidates.length === 1) {
        applyOtaEnv(candidates[0].ota_env);
        return;
      }
      setOtaEnvCandidates(candidates);
      setOtaEnvModalOpen(true);
    } catch (e: any) {
      const detail = e?.response?.data?.error || e?.message || '获取失败';
      message.error(`获取 RMP OTA Environment 失败：${detail}`);
    } finally {
      setOtaEnvLoading(false);
    }
  }, [formValues, project, applyOtaEnv]);

  const fieldSources = result?.field_sources;
  const pending = result?.pending_confirmations || [];
  const checksPending = pending.find((item) => item.path === 'checks');

  /**
   * 台架检查（bench check）默认框架：后端未返回或用户清空时用推导默认值兜底，
   * 保证「完整 JSON」与保存内容始终包含 checks 框架（version_cmds / can_channels / camera）。
   */
  const defaultChecks = result?.config?.checks ?? checksPending?.suggested;
  const rawChecks = getByPath(formValues, 'checks');
  const isEmptyChecks =
    rawChecks === undefined ||
    rawChecks === null ||
    (typeof rawChecks === 'object' &&
      !Array.isArray(rawChecks) &&
      Object.keys(rawChecks).length === 0);
  const effectiveChecks = isEmptyChecks && defaultChecks ? defaultChecks : rawChecks;
  const payload: BatConfig = { ...formValues, checks: effectiveChecks };

  const handleCopy = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      message.success('已复制到剪贴板');
    } catch {
      message.error('复制失败');
    }
  };

  const doSave = async () => {
    if (!result) return;
    setSaving(true);
    try {
      await saveBatConfig(project, payload);
      message.success('已保存到 BVP 配置中心');
      onSaved?.();
      onClose();
    } catch (e: any) {
      const detail = e?.response?.data?.error || e?.message || '保存失败';
      message.error(String(detail));
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    if (!result) return;
    const modeLabel = result.mode === 'update' ? '覆盖更新' : '新建';
    Modal.confirm({
      title: '确认保存 BAT 配置？',
      content: (
        <div>
          <p style={{ marginBottom: 4 }}>
            项目：<Text strong>{project}</Text>
          </p>
          <p style={{ marginBottom: 4 }}>
            操作：<Text strong>{modeLabel}</Text>
          </p>
          <p style={{ marginBottom: 0, color: '#8c8c8c' }}>
            将按当前页面编辑后的值写入 BVP 配置中心，影响该项目后续 BCR 任务。
          </p>
        </div>
      ),
      okText: '确认保存',
      cancelText: '取消',
      onOk: doSave,
    });
  };

  const title = (
    <Space>
      <span>运行配置</span>
      {result ? <Tag color="blue">{project}</Tag> : null}
    </Space>
  );

  return (
    <Drawer
      title={title}
      placement="right"
      styles={{ wrapper: { width: 880 } }}
      open={open}
      onClose={onClose}
      destroyOnHidden
      mask={{ closable: false }}
      footer={
        <Space style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
            重新生成
          </Button>
          <Button icon={<CopyOutlined />} onClick={handleCopy} disabled={!result}>
            复制 JSON
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={saving}
            disabled={!result || locked}
          >
            保存
          </Button>
        </Space>
      }
    >
      <Spin spinning={loading} tip="正在查询台架 / 推导规则…">
        {locked ? (
          <Alert
            type="warning"
            showIcon
            title="项目已锁定"
            description={`项目「${project}」在 BVP 配置中心已被锁定，禁止生成/修改 BAT 配置。请联系管理员解锁后再试。`}
          />
        ) : error ? (
          <Alert type="error" showIcon title="生成失败" description={error} />
        ) : result ? (
          <Space orientation="vertical" size={16} style={{ display: 'flex' }}>
            {MODE_META[result.mode] ? (
              <Alert
                type={MODE_META[result.mode].type}
                showIcon
                title={MODE_META[result.mode].text}
              />
            ) : null}

            {!getByPath(formValues, 'bench_name') ? (
              <Alert
                type="warning"
                showIcon
                title="未填写台架：无法进行准入（预约台架 / 烧录 / 台架检查），只能继续构建产物包"
              />
            ) : null}

            {result.llm_degraded_reason ? (
              <Alert
                type="warning"
                showIcon
                title="LLM 增强未启用"
                description={result.llm_degraded_reason}
              />
            ) : null}

            <div>
              <Divider titlePlacement="start" plain>
                基础信息（可编辑）
              </Divider>
              <div style={{ marginBottom: 12 }}>
                <div style={{ marginBottom: 4, fontSize: 12, color: '#595959' }}>项目</div>
                <FieldValue
                  value={formValues.project ?? project}
                  source={resolveSource(fieldSources, 'project')}
                />
              </div>
              <Row gutter={[16, 12]}>
                {EDITABLE_BASIC_FIELDS.map((field) => (
                  <Col span={12} key={field.path}>
                    <EditableField
                      label={field.label}
                      value={getByPath(formValues, field.path)}
                      source={resolveSource(fieldSources, field.path)}
                      placeholder={field.placeholder}
                      required={field.required}
                      extra={
                        field.path === 'ota_env' ? (
                          <Button
                            type="link"
                            size="small"
                            icon={<ReloadOutlined />}
                            loading={otaEnvLoading}
                            onClick={handleDiscoverOtaEnv}
                            style={{ padding: 0, height: 'auto', fontSize: 12 }}
                          >
                            获取
                          </Button>
                        ) : undefined
                      }
                      onChange={(value) => handleFieldChange(field.path, value)}
                    />
                  </Col>
                ))}
              </Row>
            </div>

            <div>
              <Divider titlePlacement="start" plain>
                规则确认（逐产物）
              </Divider>
              {artifacts.length === 0 ? (
                <Alert
                  type="info"
                  showIcon
                  title="未推导出产物规则，产物将按 AS-IS 直传"
                />
              ) : (
                <Table
                  size="small"
                  rowKey="key"
                  pagination={false}
                  dataSource={artifacts.map((key) => ({ key }))}
                  columns={[
                    {
                      title: '产物',
                      dataIndex: 'key',
                      render: (key: string) => <Text strong>{key}</Text>,
                    },
                    {
                      title: 'RMP 类型',
                      width: 120,
                      render: (_: unknown, row: { key: string }) => {
                        const deploy = isDeployEnabled(
                          row.key,
                          formValues?.kind_map,
                          formValues?.rmp_rules
                        );
                        if (!deploy) return <Tag>SKIP</Tag>;
                        const kind = resolveDeployKind(
                          row.key,
                          formValues?.kind_map,
                          formValues?.rmp_rules
                        );
                        return <Tag color="blue">{kind || '—'}</Tag>;
                      },
                    },
                    {
                      title: '默认 RMP 部署',
                      width: 140,
                      render: (_: unknown, row: { key: string }) => (
                        <Checkbox
                          checked={isDeployEnabled(
                            row.key,
                            formValues?.kind_map,
                            formValues?.rmp_rules
                          )}
                          onChange={(e) => handleToggleDeploy(row.key, e.target.checked)}
                        >
                          部署
                        </Checkbox>
                      ),
                    },
                    {
                      title: '打包规则',
                      width: 120,
                      render: (_: unknown, row: { key: string }) => {
                        const rule = formValues?.pkg_rules?.[row.key];
                        return rule && Object.keys(rule).length > 0 ? (
                          <Tag color="geekblue">自定义</Tag>
                        ) : (
                          <Text type="secondary">AS-IS 直传</Text>
                        );
                      },
                    },
                    {
                      title: '来源',
                      width: 110,
                      render: (_: unknown, row: { key: string }) => {
                        const src =
                          resolveSource(fieldSources, `rmp_rules.${row.key}`) ||
                          resolveSource(fieldSources, `kind_map.${row.key}`) ||
                          resolveSource(fieldSources, `pkg_rules.${row.key}`);
                        return src ? <SourceTag source={src} /> : <Text type="secondary">—</Text>;
                      },
                    },
                  ]}
                />
              )}
            </div>

            <div>
              <Divider titlePlacement="start" plain>
                台架检查配置（bench check）
              </Divider>
              {checksPending ? (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 8 }}
                  title="当前为推导默认值，请按台架实际情况补充"
                  description={checksPending.reason}
                />
              ) : null}
              <JsonValueField
                value={effectiveChecks}
                placeholder='如 {"version_cmds": [], "can_channels": [], "camera": {}}'
                onChange={(value) => handleFieldChange('checks', value)}
              />
            </div>

            <div>
              <div style={{ marginBottom: 8 }}>
                <Button
                  size="small"
                  type="primary"
                  ghost
                  icon={<RobotOutlined />}
                  loading={aiLoading}
                  onClick={handleAiGenerate}
                  title="按上方基础信息（含刚填写的 RMP OTA Environment）重新调用 AI 推理 kind_map / rmp_rules / pkg_rules"
                >
                  BCR运行配置AI自动生成
                </Button>
              </div>
              <Collapse
                style={{ marginTop: 12 }}
                defaultActiveKey={['full']}
                items={[
                  {
                    key: 'full',
                    label: '完整 JSON（确认无误后点右下角「保存」）',
                    children: <JsonBlock data={payload} />,
                  },
                ]}
              />
            </div>

            <Paragraph type="secondary" style={{ marginBottom: 0, fontSize: 12 }}>
              规则（kind_map / rmp_rules / pkg_rules）由 BVP 侧基于 RMP 脚本 + LLM 自动推导，
              默认 RMP 部署的产物已在上方预选；如需调整某产物的部署方式，直接勾选/取消
              「默认 RMP 部署」即可（取消 = kind 置 SKIP，仍注册 RMP 但跳过独立部署）。
              基础信息与台架检查配置已按推导结果预填，可直接修改后保存；
              台架检查留空时，将按默认框架（version_cmds / can_channels / camera）写入完整 JSON。
            </Paragraph>
          </Space>
        ) : (
          <Empty description="暂无生成结果" />
        )}
      </Spin>

      <Modal
        title="选择 RMP OTA Environment"
        open={otaEnvModalOpen}
        onCancel={() => setOtaEnvModalOpen(false)}
        footer={null}
        destroyOnHidden
      >
        <Space orientation="vertical" size={8} style={{ display: 'flex' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            按项目名 / 台架名与 RMP 记录匹配，命中多个候选，请选择正确的 RMP OTA Environment：
          </Text>
          {otaEnvCandidates.map((candidate) => (
            <Button
              key={candidate.ota_env}
              block
              onClick={() => applyOtaEnv(candidate.ota_env)}
              style={{ textAlign: 'left', height: 'auto', padding: '8px 12px' }}
            >
              <Space size={8} wrap>
                <Text strong>{candidate.ota_env}</Text>
                {(candidate.kinds || []).map((kind) => (
                  <Tag key={kind}>{kind}</Tag>
                ))}
                <Text type="secondary" style={{ fontSize: 12 }}>
                  匹配度 {((candidate.score ?? 0) * 100).toFixed(0)}%
                </Text>
              </Space>
            </Button>
          ))}
        </Space>
      </Modal>
    </Drawer>
  );
};

export default BatConfigGenerateDrawer;
